import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { BookOpen, Copy, Check, ChevronRight, X, Filter } from 'lucide-react';
import {
  IMAGE_STYLE_ATLAS,
  ATLAS_CATEGORIES,
  PACKAGE_PRESETS,
  buildAtlasPrompt,
  buildPresetAtlasEntry,
  type StyleEntry,
  type PackagePreset,
  type StylePresetLike,
} from '@/data/imageStyleAtlas';
import { parseSongMarkdown } from '@/utils/parseSongMarkdown';
import { API_BASE } from '@/config/api';
import { fetchWithCsrf } from '@utils/fetchWithCsrf';
import { ASPECT_RATIO_OPTIONS } from '@/components/PromptArchitect/constants';

interface AtlasTabProps {
  /** Song markdown from Vault / RiffWriter / Studio (title, artist, summary, lyrics) */
  songMarkdown?: string;
}

const CATEGORY_LIST = [...ATLAS_CATEGORIES] as string[];

const MOOD_LIST = [
  'earnest', 'elegant', 'glamorous', 'optimistic', 'urgent', 'intellectual',
  'joyful', 'raw', 'trippy', 'dark', 'loud', 'calm', 'hypnotic', 'nocturnal',
  'pastoral', 'heroic', 'uncanny', 'warm', 'contemplative', 'playful', 'pure',
];

function lyricsExcerpt(lyrics: string): string {
  // First non-empty non-header block, up to ~300 chars
  const clean = lyrics
    .split('\n')
    .filter(l => l.trim() && !l.trim().startsWith('[') && !l.trim().startsWith('#'))
    .join(' ')
    .trim();
  return clean.length > 300 ? clean.slice(0, 297) + '…' : clean;
}

export default function AtlasTab({ songMarkdown }: AtlasTabProps) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [filterCategory, setFilterCategory] = useState('');
  const [filterMood, setFilterMood] = useState('');
  const [filterEraPin, setFilterEraPin] = useState<PackagePreset | ''>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [chosenPreset, setChosenPreset] = useState<PackagePreset>('pkg_vinyl_classic');
  const [aspectRatio, setAspectRatio] = useState('1:1');
  const [copied, setCopied] = useState(false);
  const [stylePresets, setStylePresets] = useState<StylePresetLike[]>([]);

  useEffect(() => {
    let cancelled = false;
    const fetchPresets = async () => {
      try {
        const res = await fetchWithCsrf(`${API_BASE}/api/style-presets/list`);
        const data = await res.json();
        if (!cancelled) setStylePresets(data.presets || []);
      } catch (err) {
        console.error('Failed to load style presets for Atlas:', err);
      }
    };
    fetchPresets();
    // Style Presets created/edited/deleted elsewhere (Admin Panel) don't
    // otherwise reach an already-mounted Atlas tab — same staleness class
    // as the Vault's hookhouse:vault-updated event.
    window.addEventListener('hookhouse:style-presets-updated', fetchPresets);
    return () => {
      cancelled = true;
      window.removeEventListener('hookhouse:style-presets-updated', fetchPresets);
    };
  }, []);

  const presetStyles = useMemo(
    () => stylePresets.map((p, i) => buildPresetAtlasEntry(p, i)),
    [stylePresets]
  );

  const allStyles = useMemo(
    () => [...IMAGE_STYLE_ATLAS, ...presetStyles],
    [presetStyles]
  );

  const parsedSong = useMemo(() => {
    if (!songMarkdown?.trim()) return null;
    return parseSongMarkdown(songMarkdown);
  }, [songMarkdown]);

  const filteredStyles = useMemo(() => {
    return allStyles.filter(s => {
      if (filterCategory && s.category !== filterCategory) return false;
      if (filterMood && !s.mood.toLowerCase().includes(filterMood.toLowerCase())) return false;
      if (filterEraPin && s.packagePreset !== filterEraPin) return false;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        return (
          s.name.toLowerCase().includes(q) ||
          s.traits.toLowerCase().includes(q) ||
          s.mood.toLowerCase().includes(q) ||
          s.era.toLowerCase().includes(q) ||
          s.references.toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [allStyles, filterCategory, filterMood, filterEraPin, searchQuery]);

  const selected = useMemo(
    () => (selectedId ? allStyles.find(s => s.id === selectedId) ?? null : null),
    [allStyles, selectedId]
  );

  const handleSelect = useCallback((style: StyleEntry) => {
    setSelectedId(style.id);
    setChosenPreset(style.packagePreset);
  }, []);

  // Aging self-cancellation guard
  const presetToUse: PackagePreset =
    selected?.excludesAging && chosenPreset === 'pkg_vinyl_classic'
      ? 'pkg_modern_print'
      : chosenPreset;

  const builtPrompt = useMemo(() => {
    if (!selected) return '';
    return buildAtlasPrompt(selected, presetToUse, {
      title: parsedSong?.title || undefined,
      artist: parsedSong?.artist || undefined,
      summary: parsedSong?.summary || undefined,
      lyricsExcerpt: parsedSong?.lyrics ? lyricsExcerpt(parsedSong.lyrics) : undefined,
    }, aspectRatio);
  }, [selected, presetToUse, parsedSong, aspectRatio]);

  const handleCopy = useCallback(() => {
    if (!builtPrompt) return;
    navigator.clipboard.writeText(builtPrompt);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [builtPrompt]);

  const hasFilters = filterCategory || filterMood || filterEraPin || searchQuery;
  const clearFilters = () => {
    setFilterCategory('');
    setFilterMood('');
    setFilterEraPin('');
    setSearchQuery('');
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-accent text-slate-950 flex items-center justify-center flex-shrink-0">
          <BookOpen size={20} />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-slate-100">
            Image Style <span className="text-accent">Atlas</span>
          </h2>
          <p className="text-xs text-slate-400">
            {IMAGE_STYLE_ATLAS.length} curated{presetStyles.length > 0 ? ` + ${presetStyles.length} from your presets` : ''} · v1.4 · GPT-Image-2 ready
          </p>
        </div>
        {parsedSong?.title && (
          <div className="ml-auto text-right">
            <p className="text-xs font-medium text-slate-300 truncate max-w-[200px]">{parsedSong.title}</p>
            {parsedSong.artist && (
              <p className="text-[10px] text-slate-500 truncate max-w-[200px]">{parsedSong.artist}</p>
            )}
          </div>
        )}
      </div>

      {/* Main split panel */}
      <div className="flex gap-3 flex-1 min-h-0">

        {/* ── Left: style list ─────────────────────────────────────────── */}
        <div className="flex flex-col w-64 flex-shrink-0 min-h-0">
          {/* Filters */}
          <div className="flex flex-col gap-2 mb-2">
            <div className="relative">
              <input
                type="text"
                placeholder="Search styles…"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-1.5 text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:border-accent"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200"
                >
                  <X size={12} />
                </button>
              )}
            </div>
            <select
              value={filterCategory}
              onChange={e => setFilterCategory(e.target.value)}
              className="w-full bg-slate-800 border border-slate-700 rounded-md px-2 py-1 text-xs text-slate-300 focus:outline-none focus:border-accent"
            >
              <option value="">All categories</option>
              {CATEGORY_LIST.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <div className="flex gap-1.5">
              <select
                value={filterMood}
                onChange={e => setFilterMood(e.target.value)}
                className="flex-1 bg-slate-800 border border-slate-700 rounded-md px-2 py-1 text-xs text-slate-300 focus:outline-none focus:border-accent"
              >
                <option value="">All moods</option>
                {MOOD_LIST.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
              <select
                value={filterEraPin}
                onChange={e => setFilterEraPin(e.target.value as PackagePreset | '')}
                className="flex-1 bg-slate-800 border border-slate-700 rounded-md px-2 py-1 text-xs text-slate-300 focus:outline-none focus:border-accent"
              >
                <option value="">All eras</option>
                <option value="pkg_vinyl_classic">LP 60s–70s</option>
                <option value="pkg_vinyl_80s">LP/Cass 80s</option>
                <option value="pkg_cd_era">CD 90s–00s</option>
                <option value="pkg_modern_print">Modern</option>
              </select>
            </div>
            {hasFilters && (
              <button
                onClick={clearFilters}
                className="flex items-center gap-1 text-xs text-slate-400 hover:text-accent transition-colors"
              >
                <Filter size={10} />
                Clear ({filteredStyles.length}/{allStyles.length})
              </button>
            )}
          </div>

          {/* List */}
          <div className="flex-1 overflow-y-auto min-h-0 pr-1 space-y-0.5">
            {filteredStyles.length === 0 ? (
              <p className="text-xs text-slate-500 p-2">No styles match.</p>
            ) : (
              filteredStyles.map(style => (
                <button
                  key={style.id}
                  onClick={() => handleSelect(style)}
                  className={`w-full text-left px-2.5 py-2 rounded-lg text-xs transition-colors flex items-start gap-1.5 group ${
                    selectedId === style.id
                      ? 'bg-accent/20 border border-accent/50 text-slate-100'
                      : 'hover:bg-slate-800 text-slate-300 border border-transparent'
                  }`}
                >
                  <span className="text-slate-500 w-6 flex-shrink-0 text-right tabular-nums mt-px">
                    {style.number}.
                  </span>
                  <span className="flex-1 leading-snug">
                    <span className="font-medium">{style.name}</span>
                    <span className="block text-slate-500 truncate">{style.era}</span>
                  </span>
                  <ChevronRight
                    size={12}
                    className={`flex-shrink-0 mt-0.5 transition-opacity ${
                      selectedId === style.id
                        ? 'opacity-100 text-accent'
                        : 'opacity-0 group-hover:opacity-50'
                    }`}
                  />
                </button>
              ))
            )}
          </div>
        </div>

        {/* ── Right: dossier + package picker + prompt ─────────────────── */}
        <div className="flex-1 min-w-0 flex flex-col gap-3 overflow-y-auto">
          {!selected ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center p-8 text-slate-500">
              <BookOpen size={40} className="mb-3 opacity-30" />
              <p className="text-sm">Select a style to view its dossier and generate a prompt.</p>
              {!parsedSong && (
                <p className="text-xs mt-2 text-slate-600">
                  Tip: send a song from the Studio tab first — Atlas will fold in the title, artist, summary, and lyrics.
                </p>
              )}
            </div>
          ) : (
            <>
              {/* Dossier */}
              <div className="bg-slate-800/60 border border-slate-700 rounded-xl p-4">
                <div className="flex items-start justify-between gap-2 mb-3">
                  <div>
                    <h3 className="text-base font-semibold text-slate-100">{selected.name}</h3>
                    <p className="text-xs text-slate-400">{selected.category} · {selected.era}</p>
                  </div>
                  <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-accent/15 text-accent border border-accent/30 flex-shrink-0">
                    {selected.mood}
                  </span>
                </div>
                <DossierRow label="Traits" value={selected.traits} />
                <DossierRow label="Palette" value={selected.palette} />
                <DossierRow label="References" value={selected.references} />
                <DossierRow label="Exclude" value={selected.exclude} className="text-red-400/80" />
                {selected.caution && (
                  <DossierRow label="Caution" value={selected.caution} className="text-amber-400/80" />
                )}
                {selected.excludesAging && (
                  <p className="mt-2 text-xs text-blue-400/80 bg-blue-900/20 border border-blue-800/30 rounded-md px-2 py-1">
                    This style excludes aging/wear — package preset auto-set to Modern Print.
                  </p>
                )}
              </div>

              {/* Aspect Ratio picker */}
              <div className="bg-slate-800/60 border border-slate-700 rounded-xl p-4">
                <p className="text-xs font-semibold text-slate-300 mb-2 uppercase tracking-wide">
                  Aspect Ratio
                </p>
                <select
                  value={aspectRatio}
                  onChange={e => setAspectRatio(e.target.value)}
                  className="w-full bg-slate-800 border border-slate-700 rounded-md px-2 py-1.5 text-xs text-slate-300 focus:outline-none focus:border-accent"
                >
                  {ASPECT_RATIO_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>

              {/* Package Preset picker */}
              <div className="bg-slate-800/60 border border-slate-700 rounded-xl p-4">
                <p className="text-xs font-semibold text-slate-300 mb-2 uppercase tracking-wide">
                  Package Realism Layer
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {(Object.entries(PACKAGE_PRESETS) as [PackagePreset, typeof PACKAGE_PRESETS[PackagePreset]][]).map(([key, preset]) => {
                    const blocked = selected.excludesAging && key === 'pkg_vinyl_classic';
                    return (
                      <button
                        key={key}
                        disabled={blocked}
                        onClick={() => setChosenPreset(key)}
                        className={`text-left px-3 py-2 rounded-lg text-xs border transition-colors ${
                          blocked
                            ? 'opacity-30 cursor-not-allowed border-slate-700 bg-slate-900/40 text-slate-500'
                            : presetToUse === key
                            ? 'bg-accent/15 border-accent/50 text-slate-100'
                            : 'border-slate-700 hover:border-slate-500 text-slate-300 hover:bg-slate-700/50'
                        }`}
                      >
                        <span className="font-medium block">{preset.label}</span>
                        {blocked && (
                          <span className="text-[10px] text-red-400/70">style excludes aging</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Song context chip (if populated) */}
              {parsedSong && (parsedSong.title || parsedSong.summary) && (
                <div className="bg-slate-900/60 border border-slate-700 rounded-xl p-3">
                  <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide mb-1">
                    Song context injected into prompt
                  </p>
                  {parsedSong.title && (
                    <p className="text-xs text-slate-300 font-medium">
                      {parsedSong.title}
                      {parsedSong.artist && <span className="text-slate-500"> — {parsedSong.artist}</span>}
                    </p>
                  )}
                  {parsedSong.summary && (
                    <p className="text-xs text-slate-400 mt-0.5 line-clamp-2">{parsedSong.summary}</p>
                  )}
                  {parsedSong.lyrics && (
                    <p className="text-[10px] text-slate-500 mt-1">
                      + lyrics excerpt ({Math.min(parsedSong.lyrics.length, 300)} chars)
                    </p>
                  )}
                </div>
              )}

              {/* Generated prompt — the whole point */}
              <div className="bg-slate-950/80 border border-slate-700 rounded-xl p-4 flex-1">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs font-semibold text-slate-300 uppercase tracking-wide">
                    GPT-Image-2 Prompt
                  </p>
                  <button
                    onClick={handleCopy}
                    className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md font-medium transition-colors ${
                      copied
                        ? 'bg-green-600 text-white'
                        : 'bg-accent hover:bg-accent-dark text-slate-900'
                    }`}
                  >
                    {copied ? <Check size={12} /> : <Copy size={12} />}
                    {copied ? 'Copied!' : 'Copy Prompt'}
                  </button>
                </div>
                <pre className="text-[11px] text-slate-300 whitespace-pre-wrap leading-relaxed font-mono bg-slate-900/60 rounded-lg p-3 overflow-x-auto">
                  {builtPrompt}
                </pre>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function DossierRow({
  label, value, className = '',
}: { label: string; value: string; className?: string }) {
  return (
    <div className="mb-2">
      <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide block">{label}</span>
      <p className={`text-xs text-slate-300 leading-relaxed ${className}`}>{value}</p>
    </div>
  );
}
